import { useState, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  CheckIcon,
  FunnelIcon,
  MinusIcon,
  PlusIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from "@heroicons/react/20/solid";
import ProductCard from "./ProductCard";
import { filters, singleFilter } from "./FilterData";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Pagination,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { findProducts } from "../../../store/Product/Action";
import PriceRangeFilter from "./PriceRangeFilter";

const sortOptions = [
  { name: "Price: Low to High", value: "price_low" },
  { name: "Price: High to Low", value: "price_high" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Product() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const location = useLocation();
  const navigate = useNavigate();
  const param = useParams();
  const dispatch = useDispatch();
  //@ts-ignore
  const { products } = useSelector((store) => store);

  const decodedQueryString = decodeURIComponent(location.search);
  const searchParams = new URLSearchParams(decodedQueryString);
  const colorValue = searchParams.get("color");
  const sizeValue = searchParams.get("size");
  const priceValue = searchParams.get("price");
  const discount = searchParams.get("discount");
  const sortValue = searchParams.get("sort");
  const stock = searchParams.get("stock");
  const pageNumber = searchParams.get("pageNumber");

  const [initialMinPrice, initialMaxPrice] = priceValue
    ? priceValue.split("-").map(Number)
    : [0, 10000000];

  // Extract parentCategory from URL params (lavelOne = men/women/kids)
  const parentCategory =
    param.lavelOne &&
    (param.lavelOne === "men" ||
      param.lavelOne === "women" ||
      param.lavelOne === "kids")
      ? param.lavelOne
      : null;

  const handlePaginationChange = (event, value) => {
    const searchParams = new URLSearchParams(location.search);
    //@ts-ignore
    searchParams.set("pageNumber", value - 1);
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  const handleFilter = (value, sectionId) => {
    const searchParams = new URLSearchParams(location.search);
    let filterValue = searchParams.getAll(sectionId);

    if (filterValue.length > 0 && filterValue[0].split(",").includes(value)) {
      filterValue = filterValue[0].split(",").filter((item) => item != value);
      if (filterValue.length === 0) {
        searchParams.delete(sectionId);
      }
    } else {
      filterValue.push(value);
    }

    if (filterValue.length > 0) {
      searchParams.set(sectionId, filterValue.join(","));
    }
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  const handleRadioFilterChange = (e, sectionId) => {
    const searchParams = new URLSearchParams(location.search);
    const currentValue = searchParams.get(sectionId);
    const newValue = e.target.value;

    // If clicking the already selected radio, deselect it
    if (currentValue !== null && currentValue === newValue) {
      searchParams.delete(sectionId);
    } else {
      searchParams.set(sectionId, newValue);
    }

    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  const handleRadioLabelClick = (e, value, sectionId) => {
    const searchParams = new URLSearchParams(location.search);
    const currentValue = searchParams.get(sectionId);

    // If clicking the already selected radio label, deselect it
    if (currentValue === value) {
      e.preventDefault();
      e.stopPropagation();
      searchParams.delete(sectionId);
      const query = searchParams.toString();
      navigate({ search: `?${query}` });
    }
  };

  const handleSortChange = (sortValue) => {
    const searchParams = new URLSearchParams(location.search);
    const currentSort = searchParams.get("sort");

    // If clicking the same sort option, remove it (optional behavior)
    if (currentSort === sortValue) {
      searchParams.delete("sort");
    } else {
      searchParams.set("sort", sortValue);
    }

    // Reset to first page when sorting changes
    searchParams.set("pageNumber", "0");

    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  const handlePriceApply = (minPrice, maxPrice) => {
    const searchParams = new URLSearchParams(location.search);

    // Only set price if not default range
    if (minPrice === 0 && maxPrice === 10000) {
      searchParams.delete("price");
    } else {
      searchParams.set("price", `${minPrice}-${maxPrice}`);
    }

    // Reset to first page when filters change
    searchParams.set("pageNumber", "0");

    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  useEffect(() => {
    const [minPrice, maxPrice] =
      priceValue === null ? [0, 10000000] : priceValue.split("-").map(Number);

    const data = {
      category: param.lavelThree || "",
      colors: colorValue || [],
      sizes: sizeValue || [],
      minPrice,
      maxPrice,
      minDiscount: discount || 0,
      sort: sortValue || "price_low",
      pageNumber: pageNumber || 0,
      pageSize: 10,
      stock: stock || "",
      parentCategory: parentCategory || undefined, // Include parentCategory if available
    };

    //@ts-ignore
    dispatch(findProducts(data));
  }, [
    param.lavelThree,
    param.lavelOne,
    colorValue,
    sizeValue,
    priceValue,
    discount,
    sortValue,
    stock,
    pageNumber,
    parentCategory,
  ]);

  console.log("kjdf", products);

  return (
    <div className="bg-white">
      <div>
        {/* Mobile filter dialog */}
        <Dialog
          open={mobileFiltersOpen}
          onClose={setMobileFiltersOpen}
          className="relative z-[60] lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear data-closed:opacity-0 z-[60]"
          />

          <div className="fixed inset-0 z-[60] flex">
            <DialogPanel
              transition
              className="relative ml-auto flex size-full max-w-xs transform flex-col overflow-y-auto bg-white pt-4 pb-6 shadow-xl transition duration-300 ease-in-out data-closed:translate-x-full"
            >
              <div className="flex items-center justify-between px-4">
                <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="relative -mr-2 flex size-10 items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:outline-hidden"
                >
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>

              {/* Filters */}
              <form className="mt-4 border-t border-gray-200">
                <Disclosure
                  as="div"
                  className="border-b border-gray-200 px-4 py-6"
                >
                  <h3 className="-mx-2 -my-3 flow-root">
                    <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                      <span className="font-medium text-gray-900">
                        Price Range
                      </span>
                      <span className="ml-6 flex items-center">
                        <PlusIcon
                          aria-hidden="true"
                          className="size-5 group-data-[open]:hidden"
                        />
                        <MinusIcon
                          aria-hidden="true"
                          className="size-5 opacity-0 group-data-[open]:opacity-100"
                        />
                      </span>
                    </DisclosureButton>
                  </h3>
                  <DisclosurePanel className="pt-6">
                    <PriceRangeFilter
                      onApply={handlePriceApply}
                      initialMin={initialMinPrice}
                      initialMax={initialMaxPrice}
                    />
                  </DisclosurePanel>
                </Disclosure>
                {filters.map((section) => (
                  <Disclosure
                    key={section.id}
                    as="div"
                    className="border-t border-gray-200 px-4 py-6"
                  >
                    <h3 className="-mx-2 -my-3 flow-root">
                      <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                        <span className="font-medium text-gray-900">
                          {section.name}
                        </span>
                        <span className="ml-6 flex items-center">
                          <PlusIcon
                            aria-hidden="true"
                            className="size-5 group-data-[open]:hidden"
                          />
                          <MinusIcon
                            aria-hidden="true"
                            className="size-5 opacity-0 group-data-[open]:opacity-100"
                          />
                        </span>
                      </DisclosureButton>
                    </h3>
                    <DisclosurePanel className="pt-6">
                      <div className="space-y-4">
                        {section.options.map((option, optionIdx) => (
                          <div key={option.value} className="flex gap-3">
                            <div className="flex h-5 shrink-0 items-center">
                              <div className="group grid size-4 grid-cols-1">
                                <input
                                  onChange={() =>
                                    handleFilter(option.value, section.id)
                                  }
                                  defaultValue={option.value}
                                  defaultChecked={option.checked}
                                  id={`filter-mobile-${section.id}-${optionIdx}`}
                                  name={`${section.id}[]`}
                                  type="checkbox"
                                  className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                />
                                <CheckIcon
                                  aria-hidden="true"
                                  className="pointer-events-none col-start-1 row-start-1 z-10 size-3.5 self-center justify-self-center text-white"
                                />
                              </div>
                            </div>
                            <label
                              htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                              className="text-sm text-gray-600"
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                ))}
                {singleFilter.map((section) => (
                  <Disclosure
                    key={section.id}
                    as="div"
                    className="border-t border-gray-200 px-4 py-6"
                  >
                    <h3 className="-mx-2 -my-3 flow-root">
                      <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                        <span className="font-medium text-gray-900">
                          {section.name}
                        </span>
                        <span className="ml-6 flex items-center">
                          <PlusIcon
                            aria-hidden="true"
                            className="size-5 group-data-[open]:hidden"
                          />
                          <MinusIcon
                            aria-hidden="true"
                            className="size-5 opacity-0 group-data-[open]:opacity-100"
                          />
                        </span>
                      </DisclosureButton>
                    </h3>
                    <DisclosurePanel className="pt-6">
                      <div className="space-y-6">
                        <FormControl>
                          <RadioGroup
                            aria-labelledby={`demo-radio-buttons-group-label-mobile-${section.id}`}
                            value={searchParams.get(section.id) || ""}
                            name={`radio-buttons-group-mobile-${section.id}`}
                            onChange={(e) =>
                              handleRadioFilterChange(e, section.id)
                            }
                          >
                            {section.options.map((option, optionIdx) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={option.label}
                                onClick={(e) =>
                                  handleRadioLabelClick(
                                    e,
                                    option.value,
                                    section.id
                                  )
                                }
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                ))}
              </form>
            </DialogPanel>
          </div>
        </Dialog>

        <main className="mx-auto px-4 sm:px-6 lg:px-20">
          <div className="flex items-baseline justify-between border-b border-gray-200 pt-8 pb-6">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">
              New Arrivals
            </h1>

            <div className="flex items-center">
              <Menu as="div" className="relative inline-block text-left">
                <MenuButton className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                  {sortValue
                    ? sortOptions.find((opt) => opt.value === sortValue)
                        ?.name || "Sort"
                    : "Sort"}
                  <ChevronDownIcon
                    aria-hidden="true"
                    className="-mr-1 ml-1 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
                  />
                </MenuButton>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                >
                  <div className="py-1">
                    {sortOptions.map((option) => {
                      const isSelected = sortValue === option.value;
                      return (
                        <MenuItem key={option.name}>
                          <button
                            type="button"
                            onClick={() => handleSortChange(option.value)}
                            className={classNames(
                              isSelected
                                ? "font-medium text-gray-900"
                                : "text-gray-500",
                              "block w-full text-left px-4 py-2 text-sm data-focus:bg-gray-100 data-focus:outline-hidden"
                            )}
                          >
                            {option.name}
                          </button>
                        </MenuItem>
                      );
                    })}
                  </div>
                </MenuItems>
              </Menu>

              <div className="flex items-center ml-5 sm:ml-7">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={`-m-2 p-2 ${
                    viewMode === "grid"
                      ? "text-gray-900"
                      : "text-gray-400 hover:text-gray-500"
                  }`}
                  title="Grid view"
                >
                  <span className="sr-only">View grid</span>
                  <Squares2X2Icon aria-hidden="true" className="size-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={`-m-2 p-2 ${
                    viewMode === "list"
                      ? "text-gray-900"
                      : "text-gray-400 hover:text-gray-500"
                  }`}
                  title="List view"
                >
                  <span className="sr-only">View list</span>
                  <ListBulletIcon aria-hidden="true" className="size-5" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
              >
                <span className="sr-only">Filters</span>
                <FunnelIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>

          <section aria-labelledby="products-heading" className="pt-6 pb-24">
            <h2 id="products-heading" className="sr-only">
              Products
            </h2>

            <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-5">
              {/* Filters */}
              <div className="lg:sticky lg:top-10 lg:h-[calc(100vh-8rem)] overflow-x-hidden lg:overflow-y-auto lg:pr-2">
                <div className="py-10 hidden lg:flex justify-between items-center sticky top-0 bg-white z-10 pb-4">
                  <h1 className="text-lg opacity-50 font-bold">Filters</h1>
                  <FilterList />
                </div>
                <form className="hidden lg:block pb-4">
                  <Disclosure
                    as="div"
                    className="border-b border-gray-200 py-6"
                    defaultOpen
                  >
                    <h3 className="-my-3 flow-root">
                      <DisclosureButton className="group flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                        <span className="font-medium text-gray-900">
                          Price Range
                        </span>
                        <span className="ml-6 flex items-center relative">
                          <PlusIcon
                            aria-hidden="true"
                            className="size-5 transition-opacity duration-200 group-data-[open]:opacity-0 group-data-[open]:pointer-events-none"
                          />
                          <MinusIcon
                            aria-hidden="true"
                            className="size-5 absolute opacity-0 transition-opacity duration-200 group-data-[open]:opacity-100 group-data-[open]:pointer-events-auto"
                          />
                        </span>
                      </DisclosureButton>
                    </h3>
                    <DisclosurePanel className="pt-6 pl-3 pr-1">
                      <PriceRangeFilter
                        onApply={handlePriceApply}
                        initialMin={initialMinPrice}
                        initialMax={initialMaxPrice}
                      />
                    </DisclosurePanel>
                  </Disclosure>
                  {filters.map((section) => (
                    <Disclosure
                      key={section.id}
                      as="div"
                      className="border-b border-gray-200 py-6"
                    >
                      <h3 className="-my-3 flow-root">
                        <DisclosureButton className="group flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                          <span className="font-medium text-gray-900">
                            {section.name}
                          </span>
                          <span className="ml-6 flex items-center relative">
                            <PlusIcon
                              aria-hidden="true"
                              className="size-5 transition-opacity duration-200 group-data-[open]:opacity-0 group-data-[open]:pointer-events-none"
                            />
                            <MinusIcon
                              aria-hidden="true"
                              className="size-5 absolute opacity-0 transition-opacity duration-200 group-data-[open]:opacity-100 group-data-[open]:pointer-events-auto"
                            />
                          </span>
                        </DisclosureButton>
                      </h3>
                      <DisclosurePanel className="pt-6">
                        <div className="space-y-4">
                          {section.options.map((option, optionIdx) => (
                            <div key={option.value} className="flex gap-3">
                              <div className="flex h-5 shrink-0 items-center">
                                <div className="group grid size-4 grid-cols-1">
                                  <input
                                    onChange={() =>
                                      handleFilter(option.value, section.id)
                                    }
                                    defaultValue={option.value}
                                    defaultChecked={option.checked}
                                    id={`filter-${section.id}-${optionIdx}`}
                                    name={`${section.id}[]`}
                                    type="checkbox"
                                    className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                  />
                                  <CheckIcon
                                    aria-hidden="true"
                                    className="pointer-events-none col-start-1 row-start-1 z-10 size-3.5 self-center justify-self-center text-white"
                                  />
                                </div>
                              </div>
                              <label
                                htmlFor={`filter-${section.id}-${optionIdx}`}
                                className="text-sm text-gray-600"
                              >
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </DisclosurePanel>
                    </Disclosure>
                  ))}
                  {singleFilter.map((section) => (
                    <Disclosure
                      key={section.id}
                      as="div"
                      className="border-t border-gray-200 py-6"
                    >
                      <h3 className="-mx-2 -my-3 flow-root">
                        <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                          <FormLabel
                            sx={{ color: "black" }}
                            className="text-gray-900"
                            id="demo-radio-buttons-group-label"
                          >
                            {section.name}
                          </FormLabel>
                          <span className="ml-6 flex items-center relative">
                            <PlusIcon
                              aria-hidden="true"
                              className="size-5 transition-opacity duration-200 group-data-[open]:opacity-0 group-data-[open]:pointer-events-none"
                            />
                            <MinusIcon
                              aria-hidden="true"
                              className="size-5 absolute opacity-0 transition-opacity duration-200 group-data-[open]:opacity-100 group-data-[open]:pointer-events-auto"
                            />
                          </span>
                        </DisclosureButton>
                      </h3>
                      <DisclosurePanel className="pt-6">
                        <div className="space-y-6">
                          <FormControl>
                            <RadioGroup
                              aria-labelledby="demo-radio-buttons-group-label"
                              value={searchParams.get(section.id) || ""}
                              name="radio-buttons-group"
                              onChange={(e) =>
                                handleRadioFilterChange(e, section.id)
                              }
                            >
                              {section.options.map((option, optionIdx) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={<Radio />}
                                  label={option.label}
                                  onClick={(e) =>
                                    handleRadioLabelClick(
                                      e,
                                      option.value,
                                      section.id
                                    )
                                  }
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </DisclosurePanel>
                    </Disclosure>
                  ))}
                </form>
              </div>

              {/* Product grid */}
              <div className="lg:col-span-4 w-full">
                {products.loading ? (
                  <div className="flex justify-center items-center py-20">
                    <p className="text-lg text-gray-500">Loading products...</p>
                  </div>
                ) : products.products?.content &&
                  products.products.content.length > 0 ? (
                  <div
                    className={`bg-white py-5 ${
                      viewMode === "grid"
                        ? "flex flex-wrap justify-start"
                        : "flex flex-col space-y-4"
                    }`}
                  >
                    {products.products.content.map((item) => (
                      <ProductCard
                        key={item.id}
                        product={item}
                        viewMode={viewMode}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col justify-center items-center py-20">
                    <div className="text-center">
                      <svg
                        className="mx-auto h-24 w-24 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">
                        No products found
                      </h3>
                      <p className="mt-2 text-sm text-gray-500">
                        Try adjusting your filters or search criteria to find
                        what you're looking for.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
          <section className="w-full px=[3.6rem]">
            <div className="px-4 py-5 flex justify-center">
              <Pagination
                count={products.products?.totalPages}
                color="secondary"
                onChange={handlePaginationChange}
              />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
